// Sprint 8 T25 - Viewer read-only del BOM de un plato_template.
// Permite a la nutricionista ver que ingredientes tiene un plato APROBADO sin
// poder editarlo (los APROBADOS son inmutables; si cambia la receta, plato nuevo).
import { useEffect, useState } from 'react'
import type {
  PlatoBomItemResponse,
  PlatoTemplateResponse,
} from '../../services/nutricionista'
import { nutricionistaApi } from '../../services/nutricionista'
import { ModalShell } from '../ui/ModalShell'

interface PlatoBomViewerProps {
  plato: PlatoTemplateResponse
  onClose: () => void
}

export default function PlatoBomViewer({ plato, onClose }: PlatoBomViewerProps) {
  const [bom, setBom] = useState<PlatoBomItemResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    nutricionistaApi
      .obtenerBomPlato(plato.id)
      .then((data) => {
        if (!cancelled) setBom(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Error al cargar BOM')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [plato.id])

  const totalGramos = bom.reduce(
    (acc, item) => acc + (item.cantidad_en_gramos_calc ?? 0),
    0,
  )

  return (
    <ModalShell
      title={`Receta — ${plato.codigo}`}
      subtitle={plato.nombre_compuesto}
      onClose={onClose}
      maxWidth={640}
    >
        <p className="text-xs text-gray-500">
          Estado: <span className="uppercase font-semibold">{plato.estado}</span> · v
          {plato.version}
        </p>
        {plato.descripcion && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">
            {plato.descripcion}
          </p>
        )}
        <div className="mb-4" />

        {loading ? (
          <p className="text-sm text-gray-500 text-center py-8">Cargando ingredientes…</p>
        ) : error ? (
          <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
            {error}
          </p>
        ) : bom.length === 0 ? (
          <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center">
            <p className="text-sm text-gray-500">
              Este plato no tiene BOM registrado.
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Los platos creados antes del Sprint 8 T25 no tienen ingredientes persistidos.
              Si necesitás conocer la receta, contactá al equipo.
            </p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm border" aria-label="BOM del plato">
              <thead className="bg-gray-100 dark:bg-gray-700 text-left text-xs">
                <tr>
                  <th className="p-2 w-12">#</th>
                  <th className="p-2">Ingrediente</th>
                  <th className="p-2 w-24 text-right">Cantidad</th>
                  <th className="p-2 w-20">Unidad</th>
                  <th className="p-2 w-24 text-right">Gramos</th>
                </tr>
              </thead>
              <tbody>
                {bom.map((item) => (
                  <tr key={item.ingrediente_id} className="border-t">
                    <td className="p-2 text-xs text-gray-500">{item.orden}</td>
                    <td className="p-2">
                      <span className="font-mono text-xs text-gray-500">
                        {item.ingrediente_codigo}
                      </span>
                      <span className="ml-2">{item.ingrediente_nombre}</span>
                    </td>
                    <td className="p-2 text-right font-mono">
                      {item.cantidad.toFixed(2)}
                    </td>
                    <td className="p-2 text-xs">{item.unidad_abreviatura}</td>
                    <td className="p-2 text-right text-xs text-gray-600">
                      {item.cantidad_en_gramos_calc !== null
                        ? `${item.cantidad_en_gramos_calc.toFixed(1)} g`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              {totalGramos > 0 && (
                <tfoot className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <td colSpan={4} className="p-2 text-right text-xs font-semibold">
                      Total peso (estimado):
                    </td>
                    <td className="p-2 text-right text-xs font-bold">
                      {totalGramos.toFixed(1)} g
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
            <p className="text-xs text-gray-500 mt-2">
              Gramos solo se calcula para unidades de peso (g, kg, oz, lb).
              Volúmenes requieren densidad del ingrediente.
            </p>
          </>
        )}

        <footer className="flex justify-end pt-4 mt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="border px-4 py-2 rounded text-sm"
          >
            Cerrar
          </button>
        </footer>
    </ModalShell>
  )
}
